import { useQuery } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { useTranslation } from 'react-i18next';
import { CreditCard, TrendingUp, Clock, CheckCircle2 } from 'lucide-react';
import { PremiumPageHeader } from '@repo/ui';

const PaymentsPage = () => {
  const { t } = useTranslation();

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['student-payments-summary'],
    queryFn: () => api.get('/student/payments/summary').then((res) => res.data?.data),
  });

  const { data: payments, isLoading: paymentsLoading } = useQuery({
    queryKey: ['student-payments'],
    queryFn: () => api.get('/student/payments').then((res) => res.data?.data),
  });

  const isLoading = summaryLoading || paymentsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PremiumPageHeader
        title={t('student.payments.title')}
        subtitle={t('student.payments.subtitle', {
          defaultValue: 'Manage your invoices, track payments, and view your billing history',
        })}
        icon={CreditCard}
        iconClassName="text-primary"
        iconContainerClassName="bg-primary/10 border-primary/20"
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="stat-card rounded-xl border border-border bg-card p-5 shadow-sm">
          <p className="text-sm text-muted-foreground">{t('student.payments.total')}</p>
          <p className="text-2xl font-bold mt-1">
            {(summary?.totalAmount ?? 0).toLocaleString()} {t('common.egp')}
          </p>
        </div>
        <div className="stat-card rounded-xl border border-border bg-card p-5 shadow-sm">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-500" />
            <p className="text-sm text-muted-foreground">{t('student.payments.paid')}</p>
          </div>
          <p className="text-2xl font-bold mt-1 text-emerald-600 dark:text-emerald-400">
            {(summary?.totalPaid ?? 0).toLocaleString()} {t('common.egp')}
          </p>
        </div>
        <div className="stat-card rounded-xl border border-border bg-card p-5 shadow-sm">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-500" />
            <p className="text-sm text-muted-foreground">{t('student.payments.remaining')}</p>
          </div>
          <p className="text-2xl font-bold mt-1 text-amber-600 dark:text-amber-400">
            {(summary?.totalRemaining ?? 0).toLocaleString()} {t('common.egp')}
          </p>
        </div>
        <div className="stat-card rounded-xl border border-border bg-card p-5 shadow-sm">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-primary" />
            <p className="text-sm text-muted-foreground">{t('student.payments.orders')}</p>
          </div>
          <p className="text-2xl font-bold mt-1">{summary?.totalOrders ?? 0}</p>
          <p className="text-xs text-muted-foreground">
            {summary?.fullyPaid ?? 0} {t('student.payments.fullyPaid')}
          </p>
        </div>
      </div>

      {/* Orders List */}
      {payments?.length > 0 && (
        <div className="rounded-xl border border-border bg-card shadow-sm overflow-x-auto">
          <table className="w-full min-w-175">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-start p-4 text-sm font-medium text-muted-foreground">
                  {t('student.payments.orderNumber')}
                </th>
                <th className="text-start p-4 text-sm font-medium text-muted-foreground">
                  {t('student.payments.program')}
                </th>
                <th className="text-start p-4 text-sm font-medium text-muted-foreground">
                  {t('student.payments.amount')}
                </th>
                <th className="text-start p-4 text-sm font-medium text-muted-foreground">
                  {t('student.payments.paidAmount')}
                </th>
                <th className="text-start p-4 text-sm font-medium text-muted-foreground">
                  {t('student.payments.statusLabel')}
                </th>
                <th className="text-start p-4 text-sm font-medium text-muted-foreground">
                  {t('student.payments.date')}
                </th>
              </tr>
            </thead>
            <tbody>
              {payments.map((order: any) => (
                <tr
                  key={order._id}
                  className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                >
                  <td className="p-4 text-sm font-mono font-medium">{order.number}</td>
                  <td className="p-4 text-sm">{order.items?.[0]?.program?.track?.name || '—'}</td>
                  <td className="p-4 text-sm">
                    {(order.netTotal ?? 0).toLocaleString()} {t('common.egp')}
                  </td>
                  <td className="p-4 text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                    {(order.paidTotal ?? 0).toLocaleString()} {t('common.egp')}
                  </td>
                  <td className="p-4">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        order.remaining === 0
                          ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                          : 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
                      }`}
                    >
                      {order.remaining === 0 ? t('common.paid') : t('student.payments.partial')}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PaymentsPage;
